import {
  array,
  enum_,
  type InferOutput,
  nullable,
  number,
  object,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { type MonthlyCustomerReportAdditionItemExEntity } from '@/libs/api'
import { ROUND_TYPE, TAX_TYPE } from '@/libs/api/types'

/**
 * 月次請求レポートの追加請求の追加項目フォームスキーマ
 */
export const monthlyCustomerReportAdditionItemRowSchema = object({
  /** 追加項目ID */
  additionalPaymentCategoryId: number(VALIDATION_MESSAGES.REQUIRED_ITEM),
  /** 料金 */
  price: number(),
  /**
   * 税区分マスタ ID (VOC_PJ-462 B案、DTO 送信用)。
   * Copilot 指摘 (#3279378741) 対応: master の `taxCategoryId` が null のケースを許容するため nullable 化。
   * null 送信時は BE 側で taxType / カスケードを使って fallback 解決する。
   */
  taxCategoryId: nullable(number()),
  /** 料金 税区分 (DEPRECATED: 移行期 form value display 互換のため保持。DTO は taxCategoryId を送信) */
  taxType: enum_(TAX_TYPE),
  /** 料金 切り上げ・切り捨て */
  roundType: enum_(ROUND_TYPE),
  /** 追加項目 */
  additionalPaymentCategory: object({
    name: string()
  })
})

/**
 * 月次請求レポートの追加請求の追加項目フォーム値
 */
export type MonthlyCustomerReportAdditionItemFormValue = InferOutput<
  typeof monthlyCustomerReportAdditionItemRowSchema
>

export const initialMonthlyCustomerReportAdditionItemFormValue: MonthlyCustomerReportAdditionItemFormValue =
  {
    additionalPaymentCategoryId: 0,
    price: 0,
    taxCategoryId: null,
    taxType: TAX_TYPE.TAXABLE,
    roundType: ROUND_TYPE.ROUND_UP,
    additionalPaymentCategory: { name: '' }
  }

/**
 * 月次請求レポートの追加請求のフォームスキーマ
 */
export const monthlyCustomerReportAdditionFormValueSchema = object({
  /** 追加請求項目 */
  monthlyCustomerReportAdditionItems: array(
    monthlyCustomerReportAdditionItemRowSchema
  )
})

/**
 * 月次請求レポートの追加請求のフォーム値
 */
export type MonthlyCustomerReportAdditionFormValue = InferOutput<
  typeof monthlyCustomerReportAdditionFormValueSchema
>

/**
 * 月次請求レポートの追加請求のEntityからフォーム値に変換
 *
 * VOC_PJ-462 #1856 PR2 で API entity (`MonthlyCustomerReportAdditionItemExEntity`) から
 * `taxType` フィールドが外れた (`taxCategoryId` 化) ため、form value schema が要求する `taxType` を
 * `additionalPaymentCategory.taxType` から fallback で注入する。
 * form 表示は TaxCategory ベースだが、form value (および後続の DTO 送信) は BE 互換のため保持する。
 */
export const mapToMonthlyCustomerReportAdditionFormValue = (
  monthlyCustomerReportAdditionItems: MonthlyCustomerReportAdditionItemExEntity[]
): MonthlyCustomerReportAdditionFormValue => ({
  monthlyCustomerReportAdditionItems: monthlyCustomerReportAdditionItems.map(
    (item) => ({
      ...item,
      taxType: item.additionalPaymentCategory.taxType
    })
  )
})
